'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';


interface ResearchPublication {
  id: string;
  title: string;
  journal: string;
  date: string;
  authors: string;
  impact: string;
  url: string;
}

const ClinicalEvidence = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [selectedComplication, setSelectedComplication] = useState('all');
  const [selectedDemographic, setSelectedDemographic] = useState('all');

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const successRateData = [
  { complication: 'HIE Mild', traditional: 78, revive: 96 },
  { complication: 'HIE Moderate', traditional: 65, revive: 92 },
  { complication: 'HIE Severe', traditional: 42, revive: 85 },
  { complication: 'Neonatal Seizures', traditional: 68, revive: 93 }];


  const demographicData = [
  { demographic: 'Term (37-42w)', patients: 2800, success: 97 },
  { demographic: 'Preterm (32-36w)', patients: 1500, success: 94 },
  { demographic: 'Very Preterm (<32w)', patients: 700, success: 89 }];


  const publications: ResearchPublication[] = [
  {
    id: '1',
    title: 'Therapeutic Hypothermia for Birth Asphyxia Using a New Servo-Controlled Device—A Randomised Controlled Trial',
    journal: 'Journal of Neonatology, © 2025 National Neonatology Forum',
    date: '2025',
    authors: 'Akshaya Govind, Chaitali Raghoji and G Guruprasad',
    impact: 'Peer-Reviewed',
    url: 'https://hrauqraoxhemxiizruob.supabase.co/storage/v1/object/public/Documents/05%20Randomized%20Trial%20Report%20-govind-et-al-2025-therapeutic-hypothermia-for-birth-asphyxia-using-a-new-servo-controlled-device-a-randomised.pdf'
  }];


  const certifications = [
  { name: 'CDSCO Approved', date: 'License: MFGMD/2023/000619', icon: 'ShieldCheckIcon' },
  { name: 'ISO 13485 Certified', date: 'Medical Device Quality', icon: 'DocumentCheckIcon' },
  { name: 'Class II A Medical Device', date: 'EN ISO 13485:2016', icon: 'BuildingOffice2Icon' }];


  return (
    <section id="clinical-evidence" className="py-24 bg-background">
      <div className="max-w-[1200px] mx-auto px-6">
        <div className="text-center mb-16">
          <div className="inline-flex items-center space-x-2 bg-primary/10 text-primary px-4 py-2 rounded-2xl mb-6">
            <Icon name="AcademicCapIcon" size={20} />
            <span className="text-sm font-semibold">Evidence-Based Medicine</span>
          </div>

          <h2 className="lg:text-5xl font-bold font-heading text-foreground mb-6 text-[32px]">
            Rigorous Clinical Evidence & Research
          </h2>

          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">Our technology is validated through clinical trials in India, peer-reviewed research, and regulatory approvals from CDSCO and international medical authorities.

          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6 mb-16">
          {certifications.map((cert, index) =>
          <div key={index} className="bg-card rounded-2xl p-6 text-center hover:shadow-card transition-all duration-250 ease-out">
              <div className="p-3 bg-success/10 rounded-2xl inline-flex mb-4">
                <Icon name={cert.icon as any} size={32} className="text-success" />
              </div>
              <h3 className="font-bold text-foreground mb-2">{cert.name}</h3>
              <p className="text-sm text-muted-foreground">{cert.date}</p>
            </div>
          )}
        </div>

        

































        




















        <div className="mb-16">
          <h3 className="text-2xl font-bold font-heading text-foreground mb-8 text-center">
            Peer-Reviewed Publications
          </h3>

          <div className="space-y-4">
            {publications.map((pub) =>
            <div key={pub.id} className="bg-card rounded-2xl p-6 hover:shadow-card transition-all duration-250 ease-out">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h4 className="text-lg font-bold text-foreground mb-2">{pub.title}</h4>
                    <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground mb-2">
                      <span className="flex items-center space-x-1">
                        <Icon name="BookOpenIcon" size={16} />
                        <span>{pub.journal}</span>
                      </span>
                      <span className="flex items-center space-x-1">
                        <Icon name="CalendarIcon" size={16} />
                        <span>{pub.date}</span>
                      </span>
                      <span className="flex items-center space-x-1">
                        <Icon name="StarIcon" size={16} />
                        <span>{pub.impact}</span>
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground">{pub.authors}</p>
                  </div>
                  <a
                  href={pub.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  download
                  className="ml-4 p-2 bg-secondary text-secondary-foreground rounded-2xl hover:bg-secondary/90 transition-all duration-250 ease-out"
                  aria-label={`Download ${pub.title}`}>

                    <Icon name="ArrowDownTrayIcon" size={20} />
                  </a>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="bg-gradient-to-br from-primary to-secondary rounded-2xl p-8 text-center text-primary-foreground">
          <Icon name="DocumentTextIcon" size={48} className="mx-auto mb-4" />
          <h3 className="text-2xl font-bold font-heading mb-4">
            Access Complete Clinical Evidence Library
          </h3>
          <p className="text-lg opacity-90 mb-6 max-w-2xl mx-auto">
            Download comprehensive clinical trial data, peer-reviewed research, and regulatory documentation to support your evaluation process.
          </p>
          <a
            href="https://hrauqraoxhemxiizruob.supabase.co/storage/v1/object/public/Documents/05%20Randomized%20Trial%20Report%20-govind-et-al-2025-therapeutic-hypothermia-for-birth-asphyxia-using-a-new-servo-controlled-device-a-randomised.pdf"
            target="_blank"
            rel="noopener noreferrer"
            download
            className="inline-block bg-accent text-accent-foreground px-8 py-4 rounded-2xl font-cta font-bold text-lg shadow-cta hover:bg-accent/90 transition-all duration-250 ease-out"
          >
            Download Evidence Package
          </a>
        </div>
      </div>
    </section>);

};

export default ClinicalEvidence;